import { getEmailTemplateApiBaseUrl, getEmailTemplateHtmlUrl } from "./ContentApiConfig";

// Types for the email template API response
export interface ContentFieldValue {
  data?: string;
  image?: {
    contentUrl: string;
    title: string;
  };
}

export interface ContentField {
  name: string;
  label: string;
  contentFieldValue: ContentFieldValue;
  nestedContentFields?: ContentField[];
}

export interface EmailTemplate {
  id: number;
  uuid: string;
  dateModified: string;
  dateCreated: string;
  datePublished: string;
  languages: string[] | null;
  friendlyURL: string;
  title: string;
  contentFields: ContentField[];
  categories: string[] | null;
  customFields: unknown | null;
}

const baseUrl = getEmailTemplateApiBaseUrl();

export const getEmailTemplate = async (): Promise<EmailTemplate> => {
  const raw = await fetch(baseUrl, {
    headers: {
      Accept: "application/json",
    },
  });

  if (!raw.ok) {
    throw new Error(`Failed to fetch email template: ${raw.status} ${raw.statusText}`);
  }

  const response: unknown = await raw.json();

  // Basic validation
  if (!response || typeof response !== "object") {
    throw new Error("Invalid response from server");
  }

  return response as EmailTemplate;
};

// Function to get HTML preview for a specific email template
export const getEmailTemplateHtml = async (friendlyURL: string, loginUrl?: string): Promise<string> => {
  const url = getEmailTemplateHtmlUrl(friendlyURL);

  const raw = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ loginUrl }),
  });

  if (!raw.ok) {
    throw new Error(`Failed to fetch email template HTML: ${raw.status} ${raw.statusText}`);
  }

  return await raw.text();
};

// Helper function to extract data from content fields by name
export const getContentFieldData = (template: EmailTemplate, fieldName: string): string | undefined => {
  const field = template.contentFields.find((field) => field.name === fieldName);
  return field?.contentFieldValue?.data;
};

// Helper function to extract nested content fields (for courses)
export const getNestedContentFields = (template: EmailTemplate, fieldName: string): ContentField[] => {
  const field = template.contentFields.find((field) => field.name === fieldName);
  return field?.nestedContentFields || [];
};
